import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ShoppingBag, Phone, MapPin, MessageCircle, Package, Truck, Store } from 'lucide-react';
import { ordersService, Order, OrderStatus } from '@/services/orders';
import { formatPrice, formatDateTime, toWhatsAppPhone } from '@/utils/format';

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: 'Pendiente',
  confirmed: 'Confirmado',
  preparing: 'En preparación',
  shipped: 'Enviado',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
};

const STATUS_COLOR: Record<OrderStatus, string> = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-indigo-100 text-indigo-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-rose-100 text-rose-800',
};

const FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'confirmed', label: 'Confirmados' },
  { value: 'preparing', label: 'En preparación' },
  { value: 'shipped', label: 'Enviados' },
  { value: 'delivered', label: 'Entregados' },
  { value: 'cancelled', label: 'Cancelados' },
];

const ALL_STATUSES: OrderStatus[] = ['pending', 'confirmed', 'preparing', 'shipped', 'delivered', 'cancelled'];

export default function Orders() {
  const [filter, setFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['orders', filter],
    queryFn: () => ordersService.list(filter || undefined),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: OrderStatus }) => ordersService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['products'] }); // el stock cambió
      toast.success('Estado actualizado');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar el estado');
    },
  });

  const whatsappCustomer = (o: Order) => {
    const phone = toWhatsAppPhone(o.customer_phone);
    let msg = `¡Hola ${o.customer_name}! Te escribo por tu pedido *#${o.order_number}* (${STATUS_LABEL[o.status]}).\n`;
    msg += `Total: ${formatPrice(o.total)}.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-taupe-800 flex items-center gap-2">
          <ShoppingBag className="h-6 w-6 text-emerald-600" />
          Pedidos online
        </h1>
        <p className="text-taupe-500">Pedidos de tu tienda. Al confirmar se descuenta el stock.</p>
      </div>

      {/* Filtros */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filter === f.value ? 'bg-taupe-700 text-white' : 'bg-white border border-cream-200 text-taupe-600 hover:border-taupe-300'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-taupe-500">Cargando pedidos…</div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-cream-200 text-center py-16">
          <Package className="h-14 w-14 text-taupe-300 mx-auto mb-3" />
          <p className="text-taupe-600 font-medium">No hay pedidos</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl border border-cream-200 p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-taupe-800">#{o.order_number}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[o.status]}`}>
                      {STATUS_LABEL[o.status]}
                    </span>
                    <span className="text-xs text-taupe-400">{formatDateTime(o.created_at)}</span>
                  </div>
                  <p className="font-semibold text-taupe-800 mt-1">{o.customer_name}</p>
                  <div className="text-sm text-taupe-500 flex flex-wrap gap-x-4 gap-y-0.5">
                    <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{o.customer_phone}</span>
                    {o.customer_address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{o.customer_address}</span>}
                    <span className="inline-flex items-center gap-1">
                      {o.delivery_method === 'pickup' ? <Store className="h-3.5 w-3.5" /> : <Truck className="h-3.5 w-3.5" />}
                      {o.delivery_method === 'pickup' ? 'Retiro en tienda' : 'Envío a domicilio'}
                    </span>
                    <span className="capitalize">Pago: {o.payment_method}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-emerald-700">{formatPrice(o.total)}</p>
                </div>
              </div>

              {/* Items */}
              <div className="mt-3 border-t border-cream-100 pt-3 space-y-1">
                {o.items.map((it) => (
                  <div key={it.id} className="flex justify-between text-sm">
                    <span className="text-taupe-700">{Number(it.quantity)} x {it.product_name}</span>
                    <span className="text-taupe-600">{formatPrice(it.subtotal)}</span>
                  </div>
                ))}
              </div>

              {o.notes && <p className="mt-2 text-sm text-taupe-500 italic">Nota: {o.notes}</p>}

              {/* Acciones */}
              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <button
                  onClick={() => whatsappCustomer(o)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#25D366] text-white text-sm font-medium hover:opacity-90"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp al cliente
                </button>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-taupe-500">Estado:</label>
                  <select
                    value={o.status}
                    onChange={(e) => statusMutation.mutate({ id: o.id, status: e.target.value as OrderStatus })}
                    disabled={statusMutation.isPending}
                    className="px-3 py-2 border border-cream-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400"
                  >
                    {ALL_STATUSES.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
